import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  ArrowBack,
  Close,
  DeleteOutline,
  Edit,
  Lock,
  NoteAdd,
  Publish,
  Save,
  Visibility,
  WarningAmberOutlined,
} from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ConfirmModal from "../components/modals/ConfirmModal";
import SavingOverlay from "../components/SavingOverlay";
import RichTextEditor from "../components/RichTextEditor";
import VariablePicker, { toVariableOptions } from "../components/VariablePicker";
import { DocumentTemplateVariable } from "../services/patientDocumentsService";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useNavigation, useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography } from "../theme/designSystem";
import { printTemplateService, renderTemplateHtml, PrintTemplate } from "../services/printTemplateService";
import {
  contractErrorMessage,
  contractTemplatesService,
  ContractCustomField,
  ContractFieldType,
  ContractTemplateVersionDetail,
  ContractTemplateVersionRow,
  CONTRACT_FIELD_TYPE_LABELS,
  CONTRACT_STATUS_LABELS,
  CONTRACT_TEMPLATE_AUDIENCE_LABELS,
} from "../services/contractTemplatesService";

/**
 * Editor de uma versão do modelo de contrato (página-filha de contract-templates; ?versionId=).
 * Rascunho: texto, campos personalizados, template de impressão e nota da versão são editáveis.
 * Publicada ou arquivada: texto somente leitura (é o que o paciente aceitou); título, descrição e
 * situação do modelo seguem editáveis. "Publicar" congela e arquiva a publicada anterior.
 */

interface FieldDraft {
  originalKey: string | null;
  key: string;
  label: string;
  type: ContractFieldType;
  required: boolean;
  help: string;
  /** Uma opção por linha */
  options: string;
}

const CONTRACT_EXAMPLE =
  "{responsavel.nome}, responsável por {paciente.nome}, contrata a {clinica.nome} a partir de {campo.data_inicio}.";

const CONTRACT_INTRO =
  "Escreva a variável entre chaves no texto. Quando o contrato for emitido para um paciente, o sistema troca " +
  "cada variável pelo dado do cadastro. O que o cadastro não tem (uma data de início, um valor combinado) vira " +
  "um campo personalizado: defina o campo ao lado e insira {campo.chave} no texto; ele será perguntado no " +
  "formulário de emissão. Busque a variável no campo acima do texto e use o + para inserir no ponto do cursor.";

const slugify = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^[0-9]+/, "")
    .slice(0, 40);

const fieldKeysIn = (html: string): string[] => {
  const keys = new Set<string>();
  const token = /\{\s*campo\.([a-zA-Z0-9_]+)\s*\}/g;
  let match: RegExpExecArray | null = token.exec(html);
  while (match) {
    keys.add(match[1]);
    match = token.exec(html);
  }
  return Array.from(keys);
};

const formatDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "";

const STATUS_STYLE: Record<ContractTemplateVersionRow["status"], { bg: string; fg: string }> = {
  draft: { bg: colors.warningSoft, fg: colors.warningSoftText },
  published: { bg: colors.successSoft, fg: colors.successHover },
  archived: { bg: colors.backgroundAlt, fg: colors.textSecondary },
};

const panelSx = { p: 2, border: `1px solid ${colors.backgroundAlt}`, borderRadius: "8px", backgroundColor: colors.white } as const;
const panelTitleSx = { fontWeight: 600, color: colors.textStrong, fontSize: "0.95rem", mb: 1.5 } as const;
const fieldSx = { backgroundColor: colors.white } as const;
const topActionSx = {
  backgroundColor: colors.primary,
  color: colors.white,
  width: 40,
  height: 40,
  borderRadius: "6px",
  "&:hover": { backgroundColor: colors.primaryHover },
  "&.Mui-disabled": { backgroundColor: colors.backgroundAlt, color: colors.textMuted },
} as const;

const ContractTemplateEditor: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();
  const { navigateTo, getParam } = useRouter();
  const { toast, showToast, hideToast } = useToast();
  const versionId = getParam("versionId") || "";

  const [detail, setDetail] = useState<ContractTemplateVersionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [printTemplates, setPrintTemplates] = useState<PrintTemplate[]>([]);

  // Cabeçalho do modelo
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [active, setActive] = useState(true);

  // Versão
  const [content, setContent] = useState("");
  const [fields, setFields] = useState<ContractCustomField[]>([]);
  const [printTemplateId, setPrintTemplateId] = useState("");
  const [requiresSignature, setRequiresSignature] = useState(false);
  const [autoRenew, setAutoRenew] = useState(false);
  const [validFrom, setValidFrom] = useState("");
  const [changeNote, setChangeNote] = useState("");
  const [dirty, setDirty] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const insertIntoEditor = useRef<((text: string) => void) | null>(null);

  const [fieldDraft, setFieldDraft] = useState<FieldDraft | null>(null);
  const [fieldError, setFieldError] = useState("");
  const [fieldToDelete, setFieldToDelete] = useState<ContractCustomField | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await contractTemplatesService.getVersion(versionId);
      setDetail(data);
      setTitle(data.title);
      setDescription(data.description ?? "");
      setActive(data.templateActive);
      setContent(data.content);
      setFields(data.customFields);
      setPrintTemplateId(data.printTemplateId ?? "");
      setRequiresSignature(data.requiresSignature);
      setAutoRenew(data.autoRenew);
      setValidFrom(data.validFrom ?? "");
      setChangeNote(data.changeNote ?? "");
      setDirty(false);
      setEditorKey((k) => k + 1);
      document.title = `Clinic4Us - ${data.title} v${data.version}`;
    } catch {
      showToast("Não foi possível carregar a versão do contrato.", "error");
    } finally {
      setLoading(false);
    }
  }, [versionId, showToast]);

  useEffect(() => {
    if (versionId) load();
  }, [versionId, load]);

  useEffect(() => {
    if (!user?.entityId) return;
    printTemplateService.getByEntity(user.entityId).then(setPrintTemplates).catch(() => setPrintTemplates([]));
  }, [user?.entityId]);

  // Catálogo do público do modelo: paciente e profissional têm variáveis diferentes
  const audience = detail?.audience;
  const [variables, setVariables] = useState<DocumentTemplateVariable[]>([]);
  useEffect(() => {
    if (!audience) return;
    contractTemplatesService.getVariables(audience).then(setVariables).catch(() => setVariables([]));
  }, [audience]);

  const isDraft = detail?.status === "draft";
  // Versão congelada = modelo em uso: nada se altera por aqui (nem título/descrição); evolução é Nova versão
  const readOnlyHeader = !isDraft;
  const familyHasDraft = detail?.versions.some((v) => v.status === "draft") ?? false;
  const activePrintTemplates = useMemo(() => printTemplates.filter((t) => t.status), [printTemplates]);
  const defaultPrintTemplate = useMemo(
    () => activePrintTemplates.find((t) => t.isDefault) ?? activePrintTemplates[0] ?? null,
    [activePrintTemplates]
  );

  const variableOptions = useMemo(() => toVariableOptions(variables, fields), [variables, fields]);
  const usedKeys = useMemo(() => fieldKeysIn(content), [content]);
  const undefinedKeys = useMemo(() => usedKeys.filter((k) => !fields.some((f) => f.key === k)), [usedKeys, fields]);
  const unusedFields = useMemo(() => fields.filter((f) => !usedKeys.includes(f.key)), [fields, usedKeys]);

  const touch = <T,>(setter: React.Dispatch<React.SetStateAction<T>>) => (value: T) => { setter(value); setDirty(true); };
  const setContentDirty = touch(setContent);
  const setChangeNoteDirty = touch(setChangeNote);
  const setValidFromDirty = touch(setValidFrom);
  const setPrintTemplateDirty = touch(setPrintTemplateId);
  const setRequiresSignatureDirty = touch(setRequiresSignature);
  const setAutoRenewDirty = touch(setAutoRenew);
  const setTitleDirty = touch(setTitle);
  const setDescriptionDirty = touch(setDescription);
  const setActiveDirty = touch(setActive);

  const insertToken = (text: string) => {
    if (!isDraft) return;
    insertIntoEditor.current?.(text);
  };

  const goBack = () => {
    if (dirty) { setLeaveOpen(true); return; }
    navigateTo("contract-templates");
  };

  const persist = async (): Promise<boolean> => {
    if (!detail) return false;
    if (readOnlyHeader) return true;
    if (!title.trim()) { showToast("Informe o título do modelo.", "error"); return false; }
    setSaving(true);
    try {
      await contractTemplatesService.save({ id: detail.templateId, title: title.trim(), description: description.trim() || null, active });
      if (isDraft) {
        await contractTemplatesService.saveVersion(detail.id, {
          content: content || null,
          customFields: fields,
          printTemplateId: printTemplateId || null,
          requiresSignature,
          autoRenew,
          validFrom: validFrom || null,
          changeNote: changeNote.trim() || null,
        });
      }
      setDirty(false);
      return true;
    } catch (e) {
      showToast(contractErrorMessage(e, "Não foi possível salvar."), "error");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (await persist()) {
      showToast(isDraft ? "Rascunho salvo." : "Modelo atualizado.", "success");
      await load();
    }
  };

  const publish = async () => {
    setPublishOpen(false);
    if (!detail) return;
    if (!(await persist())) return;
    setSaving(true);
    try {
      await contractTemplatesService.publish(detail.id);
      showToast(`Versão v${detail.version} publicada. É ela que a emissão passa a usar.`, "success");
      await load();
    } catch (e) {
      showToast(contractErrorMessage(e, "Não foi possível publicar a versão."), "error");
    } finally {
      setSaving(false);
    }
  };

  const newVersion = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const id = await contractTemplatesService.createVersion(detail.templateId);
      navigateTo("contract-template-editor", { versionId: id });
    } catch (e) {
      showToast(contractErrorMessage(e, "Não foi possível criar a nova versão."), "error");
      setSaving(false);
    }
  };

  const openPreview = async () => {
    setPreviewLoading(true);
    setPreview("");
    try {
      setPreview(await contractTemplatesService.preview(content, fields));
    } catch (e) {
      setPreview(null);
      showToast(contractErrorMessage(e, "Não foi possível montar a prévia."), "error");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Campos personalizados: formulário inline no painel, cadastro em sequência
  const emptyFieldDraft = (): FieldDraft => ({ originalKey: null, key: "", label: "", type: "text", required: true, help: "", options: "" });
  const editField = (field: ContractCustomField) =>
    setFieldDraft({
      originalKey: field.key,
      key: field.key,
      label: field.label,
      type: field.type,
      required: field.required,
      help: field.help ?? "",
      options: (field.options ?? []).join("\n"),
    });

  const changeLabel = (label: string) => {
    if (!fieldDraft) return;
    // Chave sugerida pelo rótulo enquanto a pessoa não a editou à mão
    const autoKey = fieldDraft.originalKey === null && (fieldDraft.key === "" || fieldDraft.key === slugify(fieldDraft.label));
    setFieldDraft({ ...fieldDraft, label, key: autoKey ? slugify(label) : fieldDraft.key });
  };

  const commitField = () => {
    if (!fieldDraft) return;
    const key = fieldDraft.key.trim();
    const label = fieldDraft.label.trim();
    if (!label) { setFieldError("Informe o rótulo: é o que aparece no formulário de emissão."); return; }
    if (!/^[a-z][a-z0-9_]{0,39}$/.test(key)) { setFieldError("Chave inválida: letras minúsculas, dígitos e sublinhado, começando por letra."); return; }
    if (fields.some((f) => f.key === key && f.key !== fieldDraft.originalKey)) { setFieldError(`Já existe um campo com a chave "${key}".`); return; }
    const options = fieldDraft.type === "option"
      ? fieldDraft.options.split("\n").map((o) => o.trim()).filter((o) => o.length > 0)
      : null;
    if (fieldDraft.type === "option" && (options?.length ?? 0) < 2) { setFieldError("Campo de opções precisa de pelo menos duas opções, uma por linha."); return; }

    const next: ContractCustomField = { key, label, type: fieldDraft.type, required: fieldDraft.required, help: fieldDraft.help.trim() || null, options };
    setFields((prev) => {
      if (fieldDraft.originalKey === null) return [...prev, next];
      return prev.map((f) => (f.key === fieldDraft.originalKey ? next : f));
    });
    // Renomear a chave de um campo já usado no texto reescreve os tokens: o texto não fica órfão
    if (fieldDraft.originalKey && fieldDraft.originalKey !== key && usedKeys.includes(fieldDraft.originalKey)) {
      const token = new RegExp(`\\{\\s*campo\\.${fieldDraft.originalKey}\\s*\\}`, "g");
      setContent((prev) => prev.replace(token, `{campo.${key}}`));
      setEditorKey((k) => k + 1);
    }
    setDirty(true);
    setFieldError("");
    setFieldDraft(fieldDraft.originalKey === null ? emptyFieldDraft() : null);
  };

  const removeField = () => {
    if (!fieldToDelete) return;
    setFields((prev) => prev.filter((f) => f.key !== fieldToDelete.key));
    setDirty(true);
    setFieldToDelete(null);
  };

  const statusChip = (status: ContractTemplateVersionRow["status"], version: number) => {
    const style = STATUS_STYLE[status];
    return (
      <Chip
        label={`v${version} · ${CONTRACT_STATUS_LABELS[status]}`}
        size="small"
        sx={{ height: 22, fontSize: "0.72rem", fontWeight: 700, backgroundColor: style.bg, color: style.fg }}
      />
    );
  };

  const previewTemplate = printTemplateId ? activePrintTemplates.find((t) => t.id === printTemplateId) ?? defaultPrintTemplate : defaultPrintTemplate;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" sx={{ padding: "1rem", minHeight: "calc(100vh - 120px)", background: colors.background, marginTop: "85px", flex: 1 }}>
        <Container maxWidth={false} disableGutters>
          <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", mb: 2, gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, minWidth: 0 }}>
              <Tooltip title="Voltar para os modelos" arrow>
                <IconButton size="small" onClick={goBack} sx={{ mt: 0.25, color: colors.primary }} aria-label="Voltar">
                  <ArrowBack />
                </IconButton>
              </Tooltip>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" className="c4u-page-header__title" sx={{ wordBreak: "break-word" }}>
                  {detail ? detail.title : "Modelo de contrato"}
                </Typography>
                <Typography variant="body2" className="c4u-page-header__subtitle">
                  {detail
                    ? isDraft
                      ? "Rascunho: edite o texto, os campos e o template; publique quando o contrato estiver pronto."
                      : "Versão congelada: nada se altera por aqui. Para mudar o contrato, crie uma nova versão; para tirá-lo da emissão, inative o modelo na lista."
                    : "Carregando..."}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              {detail && (
                <Chip
                  label={`Destinatário: ${CONTRACT_TEMPLATE_AUDIENCE_LABELS[detail.audience]}`}
                  size="small"
                  title="Decide as variáveis do texto e em que tela o modelo é emitido; não muda depois de emitido"
                  sx={{ height: 22, fontSize: "0.72rem", fontWeight: 600, backgroundColor: detail.audience === "professional" ? "#e7e1f7" : "#e0f5f8", color: detail.audience === "professional" ? "#4a2f8f" : "#0b6a77" }}
                />
              )}
              {detail && statusChip(detail.status, detail.version)}
              {detail && dirty && (
                <Chip label="Alterações não salvas" size="small" sx={{ height: 22, fontSize: "0.72rem", backgroundColor: colors.warningSoft, color: colors.warningSoftText }} />
              )}
              <Tooltip title="Pré-visualizar com dados de exemplo (nada é gravado)" arrow>
                <span>
                  <IconButton onClick={openPreview} disabled={!detail} sx={topActionSx} aria-label="Pré-visualizar">
                    <Visibility />
                  </IconButton>
                </span>
              </Tooltip>
              {detail && !isDraft && !familyHasDraft && (
                <Tooltip title="Nova versão: cópia desta, em rascunho" arrow>
                  <IconButton onClick={newVersion} sx={topActionSx} aria-label="Nova versão">
                    <NoteAdd />
                  </IconButton>
                </Tooltip>
              )}
              <FaqButton />
            </Box>
          </Box>

          {loading || !detail ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 360px" }, gap: 2, alignItems: "start" }}>
              {/* Coluna principal: modelo e texto */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <Paper elevation={0} sx={panelSx}>
                  <Typography sx={panelTitleSx}>Modelo</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
                    <TextField
                      size="small"
                      label="Título"
                      value={title}
                      onChange={(e) => setTitleDirty(e.target.value)}
                      disabled={readOnlyHeader}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ maxLength: 120 }}
                      sx={{ ...fieldSx, flex: "1 1 280px" }}
                    />
                    <FormControlLabel
                      control={<Switch checked={active} disabled={readOnlyHeader} onChange={(e) => setActiveDirty(e.target.checked)} />}
                      label={<Typography variant="body2">{active ? "Ativo" : "Inativo"}</Typography>}
                      sx={{ ml: 0 }}
                    />
                  </Box>
                  <TextField
                    size="small"
                    label="Descrição"
                    value={description}
                    onChange={(e) => setDescriptionDirty(e.target.value)}
                    disabled={readOnlyHeader}
                    placeholder="Para que serve este contrato e quando usá-lo"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ maxLength: 500 }}
                    multiline
                    minRows={2}
                    fullWidth
                    sx={{ ...fieldSx, mt: 1.5 }}
                  />
                </Paper>

                <Paper elevation={0} sx={panelSx}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
                    <Typography sx={{ ...panelTitleSx, mb: 0 }}>Texto do contrato</Typography>
                    {!isDraft && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: colors.warningSoftText }}>
                        <Lock sx={{ fontSize: "1rem" }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>Somente leitura</Typography>
                      </Box>
                    )}
                  </Box>

                  {isDraft && (
                    <VariablePicker
                      options={variableOptions}
                      onInsert={insertToken}
                      intro={CONTRACT_INTRO}
                      examples={[{ title: "Exemplo — no texto do contrato:", template: CONTRACT_EXAMPLE }]}
                      stickyTop={85}
                    />
                  )}

                  {undefinedKeys.length > 0 && (
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, backgroundColor: colors.errorSoft, borderRadius: "6px", p: 1.25, mb: 1.5 }}>
                      <WarningAmberOutlined sx={{ color: colors.error, fontSize: "1.1rem", mt: "1px" }} />
                      <Typography variant="body2" sx={{ color: colors.errorHover }}>
                        O texto usa {undefinedKeys.map((k) => `{campo.${k}}`).join(", ")}, mas {undefinedKeys.length === 1 ? "esse campo não está definido" : "esses campos não estão definidos"}. Defina ao lado ou corrija o texto antes de salvar.
                      </Typography>
                    </Box>
                  )}

                  {isDraft ? (
                    <RichTextEditor
                      key={`contract-${detail.id}-${editorKey}`}
                      insertRef={insertIntoEditor}
                      suggestions={variableOptions}
                      value={content}
                      onChange={setContentDirty}
                      placeholder="Escreva o contrato como ele deve sair no papel. Use as variáveis e os campos personalizados onde o dado muda de paciente para paciente."
                    />
                  ) : (
                    <Box
                      sx={{
                        border: `1px solid ${colors.border}`,
                        borderRadius: "6px",
                        p: 2,
                        backgroundColor: colors.disabledBackground,
                        color: colors.textStrong,
                        lineHeight: 1.6,
                        minHeight: 200,
                        "& p": { m: 0, mb: 1 },
                        "& ul, & ol": { my: 1, pl: 3 },
                      }}
                      dangerouslySetInnerHTML={{ __html: content || "<p><em>Versão sem texto.</em></p>" }}
                    />
                  )}
                </Paper>
              </Box>

              {/* Coluna lateral: versão, campos e histórico */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <Paper elevation={0} sx={panelSx}>
                  <Typography sx={panelTitleSx}>Versão {detail.version}</Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <TextField
                      size="small"
                      label="O que mudou nesta versão"
                      value={changeNote}
                      onChange={(e) => setChangeNoteDirty(e.target.value)}
                      disabled={!isDraft}
                      placeholder={detail.version === 1 ? "Primeira versão do contrato" : "Ex.: cláusula de reajuste anual; prazo de aviso de cancelamento"}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ maxLength: 500 }}
                      multiline
                      minRows={2}
                      fullWidth
                      sx={fieldSx}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="Vale para contratos a partir de"
                      value={validFrom}
                      onChange={(e) => setValidFromDirty(e.target.value)}
                      disabled={!isDraft}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      sx={fieldSx}
                    />
                    <FormControl size="small" fullWidth disabled={!isDraft}>
                      <InputLabel shrink>Template de impressão</InputLabel>
                      <Select
                        label="Template de impressão"
                        value={printTemplateId}
                        onChange={(e) => setPrintTemplateDirty(e.target.value)}
                        notched
                        displayEmpty
                      >
                        <MenuItem value="">Padrão da clínica{defaultPrintTemplate ? ` (${defaultPrintTemplate.title})` : ""}</MenuItem>
                        {activePrintTemplates.map((t) => (
                          <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={<Checkbox size="small" checked={requiresSignature} disabled={!isDraft} onChange={(e) => setRequiresSignatureDirty(e.target.checked)} />}
                      label={<Typography variant="body2">{detail.audience === "professional" ? "Exige assinatura do profissional" : "Exige assinatura do responsável"}</Typography>}
                      sx={{ ml: 0 }}
                    />
                    <FormControlLabel
                      control={<Checkbox size="small" checked={autoRenew} disabled={!isDraft} onChange={(e) => setAutoRenewDirty(e.target.checked)} />}
                      label={
                        <Box>
                          <Typography variant="body2">Renovação automática ao fim da vigência</Typography>
                          <Typography variant="caption" sx={{ color: colors.textSecondary, display: "block" }}>
                            O texto tem cláusula de renovação: vencida a vigência, o contrato continua vigente (em ciclos do mesmo prazo) até ser cancelado.
                          </Typography>
                        </Box>
                      }
                      sx={{ ml: 0, alignItems: "flex-start", "& .MuiCheckbox-root": { pt: 0.25 } }}
                    />
                    <Box sx={{ borderTop: `1px solid ${colors.backgroundAlt}`, pt: 1.25, display: "flex", flexDirection: "column", gap: 0.25 }}>
                      <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                        Criada por <strong>{detail.createdByName || "—"}</strong> em {formatDateTime(detail.createdDate)}
                      </Typography>
                      {detail.publishedAt && (
                        <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                          Publicada por <strong>{detail.publishedByName || "—"}</strong> em {formatDateTime(detail.publishedAt)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>

                <Paper elevation={0} sx={panelSx}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                    <Typography sx={{ ...panelTitleSx, mb: 0 }}>Campos personalizados</Typography>
                    {isDraft && !fieldDraft && (
                      <Tooltip title="Novo campo" arrow>
                        <IconButton size="small" onClick={() => { setFieldError(""); setFieldDraft(emptyFieldDraft()); }} sx={{ ...topActionSx, width: 32, height: 32 }} aria-label="Novo campo">
                          <Add sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ color: colors.textSecondary, display: "block", mb: 1.5 }}>
                    Lacunas que o cadastro não preenche sozinho. Cada campo vira uma pergunta no formulário de emissão e entra no texto como <code>{"{campo.chave}"}</code>.
                  </Typography>

                  {fields.length === 0 && !fieldDraft && (
                    <Typography variant="body2" sx={{ color: colors.textSecondary, fontStyle: "italic" }}>
                      Nenhum campo ainda. Exemplos: data de início, valor da sessão, dia de vencimento.
                    </Typography>
                  )}

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    {fields.map((f) => {
                      const used = usedKeys.includes(f.key);
                      return (
                        <Box
                          key={f.key}
                          sx={{ display: "flex", alignItems: "center", gap: 1, border: `1px solid ${colors.backgroundAlt}`, borderRadius: "6px", px: 1.25, py: 0.75, backgroundColor: used ? colors.white : colors.backgroundSubtle }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: colors.textStrong, lineHeight: 1.3 }}>
                              {f.label}{f.required ? " *" : ""}
                            </Typography>
                            <Typography sx={{ fontSize: "0.72rem", color: colors.textSecondary, fontFamily: "monospace", wordBreak: "break-all" }}>
                              {`{campo.${f.key}}`} · {CONTRACT_FIELD_TYPE_LABELS[f.type]}{f.type === "option" ? ` (${f.options?.length ?? 0})` : ""}
                            </Typography>
                            {!used && (
                              <Typography sx={{ fontSize: "0.72rem", color: colors.warningSoftText }}>Não aparece no texto</Typography>
                            )}
                          </Box>
                          {isDraft && (
                            <>
                              <Tooltip title="Inserir no texto" arrow>
                                <IconButton size="small" onClick={() => insertToken(`{campo.${f.key}}`)} sx={{ color: colors.primaryText }} aria-label={`Inserir ${f.label} no texto`}>
                                  <Add sx={{ fontSize: "1rem" }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Editar campo" arrow>
                                <IconButton size="small" onClick={() => { setFieldError(""); editField(f); }} sx={{ color: colors.textSecondary }} aria-label={`Editar ${f.label}`}>
                                  <Edit sx={{ fontSize: "1rem" }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Excluir campo" arrow>
                                <IconButton size="small" onClick={() => setFieldToDelete(f)} sx={{ color: colors.error }} aria-label={`Excluir ${f.label}`}>
                                  <DeleteOutline sx={{ fontSize: "1rem" }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      );
                    })}
                  </Box>

                  {fieldDraft && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${colors.backgroundAlt}`, display: "flex", flexDirection: "column", gap: 1.25 }}>
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: colors.textStrong }}>
                        {fieldDraft.originalKey === null ? "Novo campo" : "Editar campo"}
                      </Typography>
                      <TextField
                        size="small"
                        label="Rótulo (como aparece no formulário)"
                        value={fieldDraft.label}
                        onChange={(e) => changeLabel(e.target.value)}
                        placeholder="Ex.: Data de início do atendimento"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ maxLength: 80 }}
                        autoFocus
                        fullWidth
                        sx={fieldSx}
                      />
                      <TextField
                        size="small"
                        label="Chave (no texto: {campo.chave})"
                        value={fieldDraft.key}
                        onChange={(e) => setFieldDraft({ ...fieldDraft, key: e.target.value.toLowerCase() })}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ maxLength: 40, style: { fontFamily: "monospace" } }}
                        fullWidth
                        sx={fieldSx}
                      />
                      <FormControl size="small" fullWidth>
                        <InputLabel shrink>Tipo</InputLabel>
                        <Select label="Tipo" value={fieldDraft.type} onChange={(e) => setFieldDraft({ ...fieldDraft, type: e.target.value as ContractFieldType })} notched>
                          {(Object.keys(CONTRACT_FIELD_TYPE_LABELS) as ContractFieldType[]).map((t) => (
                            <MenuItem key={t} value={t}>{CONTRACT_FIELD_TYPE_LABELS[t]}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {fieldDraft.type === "option" && (
                        <TextField
                          size="small"
                          label="Opções (uma por linha)"
                          value={fieldDraft.options}
                          onChange={(e) => setFieldDraft({ ...fieldDraft, options: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                          multiline
                          minRows={3}
                          fullWidth
                          sx={fieldSx}
                        />
                      )}
                      <TextField
                        size="small"
                        label="Ajuda (opcional)"
                        value={fieldDraft.help}
                        onChange={(e) => setFieldDraft({ ...fieldDraft, help: e.target.value })}
                        placeholder="Dica para quem preenche o formulário"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ maxLength: 200 }}
                        fullWidth
                        sx={fieldSx}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={fieldDraft.required} onChange={(e) => setFieldDraft({ ...fieldDraft, required: e.target.checked })} />}
                        label={<Typography variant="body2">Obrigatório na emissão</Typography>}
                        sx={{ ml: 0 }}
                      />
                      {fieldError && <Typography variant="body2" sx={{ color: colors.error }}>{fieldError}</Typography>}
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                        <Button size="small" variant="outlined" onClick={() => { setFieldDraft(null); setFieldError(""); }} sx={{ textTransform: "none", color: colors.textSecondary, borderColor: colors.textSecondary }}>
                          Cancelar
                        </Button>
                        <Button size="small" variant="contained" onClick={commitField} sx={{ textTransform: "none", backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } }}>
                          {fieldDraft.originalKey === null ? "Adicionar" : "Aplicar"}
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {unusedFields.length > 0 && !fieldDraft && isDraft && (
                    <Typography variant="caption" sx={{ display: "block", mt: 1.25, color: colors.warningSoftText }}>
                      Campo que não aparece no texto não é perguntado na emissão. Insira-o ou exclua-o.
                    </Typography>
                  )}
                </Paper>

                <Paper elevation={0} sx={panelSx}>
                  <Typography sx={panelTitleSx}>Versões deste modelo</Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    {detail.versions.map((v) => {
                      const current = v.id === detail.id;
                      return (
                        <Box
                          key={v.id}
                          component={current ? "div" : "button"}
                          type={current ? undefined : "button"}
                          onClick={current ? undefined : () => navigateTo("contract-template-editor", { versionId: v.id })}
                          sx={{
                            display: "flex", alignItems: "center", gap: 1, width: "100%", textAlign: "left",
                            border: `1px solid ${current ? colors.primary : colors.backgroundAlt}`, borderRadius: "6px", px: 1.25, py: 0.75,
                            backgroundColor: current ? colors.primaryLight : colors.white, cursor: current ? "default" : "pointer", font: "inherit",
                            "&:hover": current ? undefined : { borderColor: colors.borderHover },
                            "&:focus-visible": { outline: `2px solid ${colors.primary}`, outlineOffset: 2 },
                          }}
                        >
                          {statusChip(v.status, v.version)}
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {v.changeNote || (v.status === "published" ? `Publicada em ${formatDateTime(v.publishedAt)}` : `Criada em ${formatDateTime(v.createdDate)}`)}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              </Box>
            </Box>
          )}

          {detail && !loading && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 2, pt: 2, borderTop: `1px solid ${colors.backgroundAlt}` }}>
              <Button onClick={goBack} variant="outlined" disabled={saving} sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}>
                Voltar
              </Button>
              {isDraft && (
                <Button
                  variant="outlined"
                  startIcon={<Save />}
                  onClick={save}
                  disabled={saving}
                  sx={{ textTransform: "none", borderColor: colors.primary, color: colors.primaryText, "&:hover": { borderColor: colors.primaryHover, backgroundColor: colors.primaryLight } }}
                >
                  {saving ? "Salvando..." : "Salvar rascunho"}
                </Button>
              )}
              {isDraft && (
                <Button
                  variant="contained"
                  startIcon={<Publish />}
                  onClick={() => setPublishOpen(true)}
                  disabled={saving}
                  sx={{ textTransform: "none", backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } }}
                >
                  Salvar e publicar
                </Button>
              )}
            </Box>
          )}
        </Container>
      </Box>

      {/* Prévia: página A4 com o template da clínica e o texto com exemplos */}
      <Dialog open={preview !== null} onClose={() => setPreview(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}>
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="span" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold }}>
            Prévia com dados de exemplo
          </Typography>
          <IconButton onClick={() => setPreview(null)} sx={{ color: colors.white, padding: "0.25rem" }} aria-label="Fechar"><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", backgroundColor: colors.backgroundSubtle }}>
          <Typography variant="caption" sx={{ display: "block", color: colors.textSecondary, mb: 1.5 }}>
            Variáveis com valores de exemplo; cada campo personalizado aparece destacado com o rótulo que será perguntado na emissão.
          </Typography>
          {previewLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress sx={{ color: colors.primary }} /></Box>
          ) : (
            <Box sx={{ backgroundColor: colors.white, boxShadow: "0 2px 12px rgba(0,0,0,0.12)", p: { xs: 2, md: "15mm" }, mx: "auto", maxWidth: "210mm", color: "#000", fontSize: "12pt", lineHeight: 1.6 }}>
              {previewTemplate && (
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
                    {previewTemplate.file ? <img src={previewTemplate.file} alt="logo" style={{ maxHeight: "30mm", maxWidth: "70mm", objectFit: "contain" }} /> : <span />}
                    <Box sx={{ textAlign: "right", fontSize: "10pt", lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: renderTemplateHtml(previewTemplate.header) }} />
                  </Box>
                  <Box component="hr" sx={{ border: "none", borderTop: "1px solid #999", my: "6mm" }} />
                </Box>
              )}
              <Box
                sx={{ "& p": { m: 0, mb: "2mm" }, "& ul, & ol": { my: "2mm", pl: "8mm" }, "& mark.contract-field": { backgroundColor: colors.warningSoft, color: colors.warningSoftText, fontWeight: 600, padding: "0 3px", borderRadius: "3px" } }}
                dangerouslySetInnerHTML={{ __html: preview || "<p><em>Sem texto.</em></p>" }}
              />
              {previewTemplate && (
                <Box sx={{ mt: "12mm", textAlign: "center", fontSize: "9pt", pt: "6mm", borderTop: "1px solid #ddd" }} dangerouslySetInnerHTML={{ __html: renderTemplateHtml(previewTemplate.footer) }} />
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmModal
        isOpen={publishOpen}
        title={`Salvar e publicar a versão ${detail?.version ?? ""}`}
        message={
          detail?.versions.some((v) => v.status === "published")
            ? "As alterações são salvas e a versão é publicada: a publicada atual fica arquivada e esta passa a ser a que a emissão usa. Depois disso o texto e os campos não mudam mais; alteração exige uma nova versão."
            : "As alterações são salvas e a versão é publicada. Depois disso o texto e os campos não mudam mais; alteração exige uma nova versão."
        }
        type="info"
        confirmButtonText="Publicar"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={publish}
        onCancel={() => setPublishOpen(false)}
      />

      <ConfirmModal
        isOpen={fieldToDelete !== null}
        title="Excluir campo personalizado"
        message={
          fieldToDelete && usedKeys.includes(fieldToDelete.key)
            ? `O campo "${fieldToDelete.label}" ainda aparece no texto como {campo.${fieldToDelete.key}}. Excluir a definição deixa o texto com um campo indefinido; retire-o do texto antes de salvar.`
            : `Excluir o campo "${fieldToDelete?.label ?? ""}"?`
        }
        type="warning"
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        onConfirm={removeField}
        onCancel={() => setFieldToDelete(null)}
      />

      <ConfirmModal
        isOpen={leaveOpen}
        title="Sair sem salvar?"
        message="Há alterações não salvas nesta versão. Ao sair, elas serão perdidas."
        type="warning"
        confirmButtonText="Sair sem salvar"
        cancelButtonText="Continuar editando"
        onConfirm={() => { setLeaveOpen(false); navigateTo("contract-templates"); }}
        onCancel={() => setLeaveOpen(false)}
      />

      <SavingOverlay open={saving} message="Salvando..." />
      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default ContractTemplateEditor;
